import { Star, User, Eye, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';

export interface TemplateItem {
  id: string;
  source: 'platform' | 'tenant';
  name: string;
  description: string | null;
  tags: string[];
  triggerConfig?: { triggerType: string; eventType?: string | null } | null;
}

interface TemplateCardProps {
  template: TemplateItem;
  onUse: (template: TemplateItem) => void;
  onPreview?: (template: TemplateItem) => void;
  onEdit?: (template: TemplateItem) => void;
  onDelete?: (template: TemplateItem) => void;
}

const TAG_COLORS: Record<string, string> = {
  loyalty: 'bg-violet-100 text-violet-700',
  onboarding: 'bg-emerald-100 text-emerald-700',
  retention: 'bg-amber-100 text-amber-700',
  're-engagement': 'bg-amber-100 text-amber-700',
  vip: 'bg-pink-100 text-pink-700',
  content: 'bg-sky-100 text-sky-700',
  engagement: 'bg-indigo-100 text-indigo-700',
};

function tagColor(tag: string) {
  return TAG_COLORS[tag] ?? 'bg-zinc-100 text-zinc-600';
}

export function TemplateCard({ template, onUse, onPreview, onEdit, onDelete }: TemplateCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isPlatform = template.source === 'platform';

  return (
    <div className="relative border border-zinc-200 rounded-xl p-4 bg-white hover:border-violet-200 hover:shadow-sm transition-all group">
      {/* Header: badge + menu */}
      <div className="flex items-start justify-between mb-3">
        {isPlatform ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
            <Star size={9} /> PLATFORM
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
            <User size={9} /> MINE
          </span>
        )}

        {!isPlatform && (onEdit || onDelete) && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
              className="p-1 text-zinc-300 hover:text-zinc-600 rounded transition-colors"
            >
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-6 z-20 bg-white border border-zinc-200 rounded-lg shadow-lg min-w-[120px] py-1 text-xs">
                  {onEdit && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit(template); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-zinc-700 hover:bg-zinc-50"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(template); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Name + description */}
      <div className="mb-2">
        <div className="text-sm font-semibold text-zinc-900 mb-1">{template.name}</div>
        {template.description && (
          <div className="text-xs text-zinc-500 line-clamp-2">{template.description}</div>
        )}
      </div>

      {/* Tags */}
      {template.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {template.tags.map((tag) => (
            <span key={tag} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${tagColor(tag)}`}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Trigger hint */}
      {template.triggerConfig?.eventType && (
        <div className="text-[10px] text-zinc-400 mb-3 font-mono">
          trigger: {template.triggerConfig.eventType}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onUse(template)}
          className="flex-1 py-1.5 text-xs font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          Use template
        </button>
        {onPreview && (
          <button
            onClick={() => onPreview(template)}
            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
            title="Preview"
          >
            <Eye size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
